import React, { useId, useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  DescriptionOutlined as WordIcon,
  PictureAsPdfOutlined as PdfIcon,
  CodeOutlined as HtmlIcon,
  SlideshowOutlined as PptxIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import enhancedExportService from '../services/enhancedExportService';
import pptxExportService from '../services/pptxExportService';
import { buildFieldContext } from '../utils/pageSetup';

/** Human labels for the export progress text. */
const EXPORT_LABELS = { word: 'Word', pdf: 'PDF', html: 'HTML', pptx: 'PowerPoint' };

/**
 * Export button with a format menu (Word, PDF, HTML and optionally PowerPoint).
 * @param {object} props - Props.
 * @param {string} props.htmlContent - Document HTML.
 * @param {string} props.fileName - File name (extension replaced per format).
 * @param {string} [props.projectName] - Project name for the export header.
 * @param {'contained'|'outlined'|'text'} [props.variant='contained'] - Button variant.
 * @param {'small'|'medium'|'large'} [props.size='medium'] - Button size.
 * @param {boolean} [props.disabled] - Disable the button.
 * @param {string} [props.buttonText='Export'] - Button label.
 * @param {object} [props.sx] - Button styles.
 * @param {boolean} [props.enablePptx=false] - Offer PowerPoint export.
 * @param {object|null} [props.pageSetup] - The document's page_setup (headers, footers, margins,
 *   orientation) for Word, PDF and HTML; null or omitted = defaults. PowerPoint ignores it.
 * @param {object|null} [props.sourceDocument] - The generated document row (PowerPoint export reads
 *   the template theme saved on it).
 * @param {object} [props.fieldContext] - Live-field values for the header and footer
 *   (`buildFieldContext`: title, customer, project, date).
 * @returns {JSX.Element} Button and menu.
 */
const ExportDropdown = ({
  htmlContent,
  fileName,
  projectName = '',
  variant = 'contained',
  size = 'medium',
  disabled = false,
  buttonText = 'Export',
  sx = {},
  enablePptx = false,
  pageSetup = null,
  fieldContext,
  sourceDocument = null,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState('');
  const open = Boolean(anchorEl);
  const baseId = useId();
  const buttonId = `${baseId}-export-button`;
  const menuId = `${baseId}-export-menu`;

  const handleExport = async (type) => {
    if (!htmlContent) return;

    setExporting(true);
    setExportType(type);
    setAnchorEl(null);

    try {
      const baseFileName = String(fileName || 'document').replace(/\.(docx|pdf|html|pptx)$/i, '');
      const pageOptions = {
        pageSetup,
        fieldContext: fieldContext || buildFieldContext({ title: baseFileName, project: projectName }),
      };

      switch (type) {
        case 'word':
          await enhancedExportService.exportToWord(htmlContent, `${baseFileName}.docx`, projectName, pageOptions);
          break;
        case 'pdf':
          await enhancedExportService.exportToPDF(htmlContent, `${baseFileName}.pdf`, projectName, pageOptions);
          break;
        case 'html':
          await enhancedExportService.exportToHTML(htmlContent, `${baseFileName}.html`, projectName, pageOptions);
          break;
        case 'pptx':
          if (!enablePptx) throw new Error('Unknown export type');
          await pptxExportService.exportToPptx(htmlContent, `${baseFileName}.pptx`, projectName, { document: sourceDocument });
          break;
        default:
          throw new Error('Unknown export type');
      }
    } catch (error) {
      console.error(`${type.toUpperCase()} export failed:`, error);
    } finally {
      setExporting(false);
      setExportType('');
    }
  };

  return (
    <>
      <Button
        id={buttonId}
        variant={variant}
        size={size}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={disabled || exporting || !htmlContent}
        sx={sx}
        startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
        endIcon={exporting ? null : <ArrowDownIcon />}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
      >
        {exporting ? `Exporting ${EXPORT_LABELS[exportType] || ''}…` : buttonText}
      </Button>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ 'aria-labelledby': buttonId }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {enablePptx && (
          <MenuItem onClick={() => handleExport('pptx')}>
            <ListItemIcon><PptxIcon fontSize="small" /></ListItemIcon>
            <ListItemText>PowerPoint (.pptx)</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleExport('word')}>
          <ListItemIcon><WordIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Word (.docx)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('pdf')}>
          <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText>PDF (.pdf)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('html')}>
          <ListItemIcon><HtmlIcon fontSize="small" /></ListItemIcon>
          <ListItemText>HTML (.html)</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ExportDropdown;
